import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useCallback } from 'react';
import { api } from '@/lib/apiClient';
import { useAuth } from '@/contexts/AuthContext';
import { useSyncState } from './useSyncState';
import {
  STORES,
  putMany,
  getAll,
  put,
  addPendingAction,
} from '@/lib/offlineDB';
import { updatePendingCount } from '@/lib/syncService';
import { toast } from 'sonner';
import logger from '@/lib/logger';

export interface WorkOrderItem {
  id: string;
  work_order_id: string;
  item_number: number;
  part_label?: string;
  status: 'pending' | 'in_progress' | 'completed' | 'on_hold' | 'cancelled';
  assigned_operator_id?: string;
  started_at?: string;
  completed_at?: string;
  notes?: string;
}

export interface WorkOrder {
  id: string;
  order_number: string;
  product_code?: string;
  product_name: string;
  customer_id?: string | null;
  customer?: string;
  assigned_operator?: string | null;
  quantity: number;
  status: 'pending' | 'in_progress' | 'completed' | 'cancelled';
  priority: number;
  plybook_id?: string | null;
  project_id?: string | null;
  part_id?: string | null;
  custom_project_name?: string | null;
  created_at: string;
  updated_at?: string;
  process_records?: ProcessRecord[];
  work_order_items?: WorkOrderItem[];
  // Relations
  plybook?: {
    id: string;
    name: string;
    code?: string;
    total_plies: number;
  };
  project?: {
    id: string;
    name: string;
    reference_code?: string;
  };
  // Offline metadata
  _offline?: boolean;
  _pendingSync?: boolean;
}

export interface ProcessRecord {
  id: string;
  work_order_id: string;
  process_id?: string;
  manufacturing_process_id?: string;
  operator_id?: string;
  assigned_skill_level?: string; // Para asignaciones genéricas: 'A', 'B', 'C', 'any'
  status: 'pending' | 'in_progress' | 'paused' | 'completed';
  started_at?: string;
  completed_at?: string;
  notes?: string;
  manufacturing_processes?: {
    id?: string;
    name: string;
    code?: string;
    description?: string;
    sequence_order?: number;
  };
  work_orders?: {
    id?: string;
    order_number: string;
    product_name: string;
    customer?: string;
    mold_id?: string;
    mold?: {
      id: string;
      name: string;
      code?: string;
      location?: string;
      nfc_tag_uid?: string;
    } | null;
    plybook?: {
      id: string;
      name: string;
      code?: string;
    } | null;
    linked_plotter_cut_id?: string;
  };
  // Offline metadata
  _offline?: boolean;
  _pendingSync?: boolean;
}

// Hook for fetching work orders with offline support
export function useWorkOrdersOffline() {
  const { user } = useAuth();
  const { isOnline } = useSyncState();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['work-orders'],
    queryFn: async () => {
      if (!isOnline) {
        // Return cached data when offline
        logger.info('[useWorkOrdersOffline] Offline - using cached data');
        const cached = await getAll<WorkOrder>(STORES.WORK_ORDERS);
        return cached;
      }

      try {
        const { data, error } = await api
          .from('work_orders')
          .select(`
            *,
            process_records(id, status),
            work_order_items(id, item_number, status, part_label)
          `)
          .order('created_at', { ascending: false });

        if (error) throw error;

        // Cache the data for offline use
        if (data && data.length > 0) {
          await putMany(STORES.WORK_ORDERS, data);
          logger.info(`[useWorkOrdersOffline] Cached ${data.length} work orders`);
        }

        return data as WorkOrder[];
      } catch (error) {
        // If fetch fails, try to use cached data
        logger.warn('[useWorkOrdersOffline] Fetch failed, using cached data');
        const cached = await getAll<WorkOrder>(STORES.WORK_ORDERS);
        if (cached.length > 0) {
          toast.info('Usando datos en caché');
          return cached;
        }
        throw error;
      }
    },
    enabled: !!user,
    staleTime: 30000, // 30 seconds
    refetchOnWindowFocus: true,
    retry: isOnline ? 3 : 0, // Don't retry when offline
  });

  // Refresh from cache when going offline
  useEffect(() => {
    if (!isOnline) {
      queryClient.invalidateQueries({ queryKey: ['work-orders'] });
    }
  }, [isOnline, queryClient]);

  return {
    ...query,
    isOfflineData: !isOnline && query.data !== undefined,
  };
}

// Hook for fetching operator's process records with offline support
export function useOperatorProcessesOffline() {
  const { user } = useAuth();
  const { isOnline } = useSyncState();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['operator-processes', user?.id],
    queryFn: async () => {
      if (!isOnline) {
        // Return cached data when offline
        logger.info('[useOperatorProcessesOffline] Offline - using cached data');
        const cached = await getAll<ProcessRecord>(STORES.PROCESS_RECORDS);
        return cached.filter(r =>
          r.operator_id === user?.id &&
          ['pending', 'in_progress', 'paused'].includes(r.status)
        );
      }

      try {
        const { data, error } = await api
          .from('process_records')
          .select('*, manufacturing_processes(id, name, code, description, sequence_order), work_orders(id, order_number, product_name, customer, mold_id, plybook_id, linked_plotter_cut_id)')
          .eq('operator_id', user?.id)
          .in('status', ['pending', 'in_progress', 'paused'])
          .order('created_at', { ascending: false });

        if (error) throw error;

        // Cache the data for offline use
        if (data && data.length > 0) {
          await putMany(STORES.PROCESS_RECORDS, data);
          logger.info(`[useOperatorProcessesOffline] Cached ${data.length} process records`);
        }

        return data as ProcessRecord[];
      } catch (error) {
        // If fetch fails, try to use cached data
        logger.warn('[useOperatorProcessesOffline] Fetch failed, using cached data');
        const cached = await getAll<ProcessRecord>(STORES.PROCESS_RECORDS);
        const filtered = cached.filter(r =>
          r.operator_id === user?.id &&
          ['pending', 'in_progress', 'paused'].includes(r.status)
        );
        if (filtered.length > 0) {
          toast.info('Usando datos en caché');
          return filtered;
        }
        throw error;
      }
    },
    enabled: !!user,
    staleTime: 15000, // 15 seconds - more frequent updates for operator view
    refetchOnWindowFocus: true,
    retry: isOnline ? 3 : 0,
  });

  // Refresh from cache when going offline
  useEffect(() => {
    if (!isOnline) {
      queryClient.invalidateQueries({ queryKey: ['operator-processes', user?.id] });
    }
  }, [isOnline, queryClient, user?.id]);

  return {
    ...query,
    isOfflineData: !isOnline && query.data !== undefined,
  };
}

// Hook for updating process records with offline support
export function useUpdateProcessOffline() {
  const { isOnline } = useSyncState();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({
      recordId,
      updates,
    }: {
      recordId: string;
      updates: Partial<ProcessRecord>;
    }) => {
      if (isOnline) {
        // Try to update directly
        const { data, error } = await api
          .from('process_records')
          .update(updates)
          .eq('id', recordId)
          .select()
          .single();

        if (error) throw error;
        return { data, offline: false };
      } else {
        // Queue for later sync
        await addPendingAction({
          type: 'update',
          entity: 'process_record',
          endpoint: `/process_records?id=eq.${recordId}`,
          method: 'PATCH',
          data: updates,
        });

        // Update local cache
        const cached = await getAll<ProcessRecord>(STORES.PROCESS_RECORDS);
        const index = cached.findIndex(r => r.id === recordId);
        if (index >= 0) {
          cached[index] = { ...cached[index], ...updates, _pendingSync: true };
          await put(STORES.PROCESS_RECORDS, cached[index]);
        }

        await updatePendingCount();
        return { data: { id: recordId, ...updates }, offline: true };
      }
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['operator-processes'] });
      queryClient.invalidateQueries({ queryKey: ['work-orders'] });

      if (result.offline) {
        toast.success('Cambio guardado localmente', {
          description: 'Se sincronizará cuando haya conexión',
        });
      }
    },
    onError: async (error: any, variables) => {
      // If network error, queue the action
      if (error.name === 'TypeError' || !navigator.onLine) {
        await addPendingAction({
          type: 'update',
          entity: 'process_record',
          endpoint: `/process_records?id=eq.${variables.recordId}`,
          method: 'PATCH',
          data: variables.updates,
        });
        await updatePendingCount();
        toast.info('Sin conexión - cambio guardado localmente');
      } else {
        toast.error('Error al actualizar proceso');
        logger.error('[useUpdateProcessOffline] Error:', error);
      }
    },
  });

  return mutation;
}

// Hook for creating work orders with offline support
export function useCreateWorkOrderOffline() {
  const { isOnline } = useSyncState();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (workOrder: Omit<WorkOrder, 'id' | 'created_at'>) => {
      const newOrder: WorkOrder = {
        ...workOrder,
        id: crypto.randomUUID(),
        created_at: new Date().toISOString(),
        _offline: !isOnline,
        _pendingSync: !isOnline,
      };

      if (isOnline) {
        // Try to create directly
        const { data, error } = await api
          .from('work_orders')
          .insert(newOrder)
          .select()
          .single();

        if (error) throw error;
        return { data, offline: false };
      } else {
        // Save locally and queue for sync
        await put(STORES.WORK_ORDERS, newOrder);
        await addPendingAction({
          type: 'create',
          entity: 'work_order',
          endpoint: '/work_orders',
          method: 'POST',
          data: newOrder,
        });
        await updatePendingCount();
        return { data: newOrder, offline: true };
      }
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['work-orders'] });

      if (result.offline) {
        toast.success('Orden creada localmente', {
          description: 'Se sincronizará cuando haya conexión',
        });
      } else {
        toast.success('Orden de trabajo creada');
      }
    },
    onError: async (error: any, variables) => {
      // If network error, save offline
      if (error.name === 'TypeError' || !navigator.onLine) {
        const newOrder: WorkOrder = {
          ...variables,
          id: crypto.randomUUID(),
          created_at: new Date().toISOString(),
          _offline: true,
          _pendingSync: true,
        } as WorkOrder;

        await put(STORES.WORK_ORDERS, newOrder);
        await addPendingAction({
          type: 'create',
          entity: 'work_order',
          endpoint: '/work_orders',
          method: 'POST',
          data: newOrder,
        });
        await updatePendingCount();

        queryClient.invalidateQueries({ queryKey: ['work-orders'] });
        toast.info('Sin conexión - orden guardada localmente');
      } else {
        toast.error('Error al crear orden de trabajo');
        logger.error('[useCreateWorkOrderOffline] Error:', error);
      }
    },
  });

  return mutation;
}

// Hook for fetching available pool work matching operator's skill level
export function useAvailablePoolWork() {
  const { user, profile } = useAuth();
  const { isOnline } = useSyncState();

  const query = useQuery({
    queryKey: ['pool-work', user?.id, profile?.skill_level],
    queryFn: async () => {
      if (!isOnline) {
        return [];
      }

      try {
        // Buscar procesos sin operador asignado (pool)
        const { data, error } = await api
          .from('process_records')
          .select('*, manufacturing_processes(id, name, code, description, sequence_order), work_orders(id, order_number, product_name, customer, status, mold_id, plybook_id, linked_plotter_cut_id)')
          .is('operator_id', null)
          .eq('status', 'pending')
          .in('work_orders.status', ['pending', 'in_progress'])
          .order('created_at', { ascending: true })
          .limit(20);

        if (error) throw error;

        return (data || []) as ProcessRecord[];
      } catch (error) {
        logger.error('[useAvailablePoolWork] Error:', error);
        return [];
      }
    },
    enabled: !!user && isOnline,
    staleTime: 30000,
    refetchOnWindowFocus: true,
  });

  return query;
}

// Hook para reclamar trabajo del pool
export function useClaimPoolWork() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({ recordId }: { recordId: string }) => {
      const { data, error } = await api
        .from('process_records')
        .update({
          operator_id: user?.id,
          claimed_at: new Date().toISOString(),
        })
        .eq('id', recordId)
        .is('operator_id', null)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pool-work'] });
      queryClient.invalidateQueries({ queryKey: ['operator-processes'] });
      toast.success('Trabajo asignado');
    },
    onError: (error: any) => {
      if (error.message?.includes('0 rows')) {
        toast.error('Este trabajo ya fue tomado por otro operario');
      } else {
        toast.error('Error al reclamar trabajo');
      }
      logger.error('[useClaimPoolWork] Error:', error);
    },
  });

  return mutation;
}
